import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "./apiBase";

const AuthContext = createContext(null);

const STORAGE_KEY = "ficopilot_auth_v1";
const API = API_BASE;

const api = axios.create({
  baseURL: API,
  withCredentials: true, // send session_token + access_token cookies
});

// ---------- Global auth-error interceptor ----------------------------------
// Any API call that returns 401 `not_authenticated` or 403 `account_deactivated`
// is treated as "your session is dead": clear the local cache + Supabase
// session + cookies, then surface a clean DE/EN toast and redirect to /login.
// This guarantees there is no zombie "half-logged-in" state where the URL
// still shows a profile route but every API call is failing.
let _authBroadcastedAt = 0;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const raw = err?.response?.data?.detail;
    const code = typeof raw === "string" ? raw : (raw?.error || "");
    const url = err?.config?.url || "";
    // /auth/me is a *probe* — 401 there just means "no session yet" on a
    // fresh tab and must NOT trigger the kicked-out toast/redirect.
    const isProbe = url.includes("/auth/me") || url.includes("/auth/refresh");
    const isAuthFail =
      !isProbe && (
        (status === 401 && (code === "not_authenticated" || code === "auth_required" || raw === "not_authenticated"))
        || (status === 403 && (code === "account_deactivated" || raw === "account_deactivated"))
      );
    if (isAuthFail) {
      // Debounce: don't fire 10 toasts from a burst of failing parallel calls.
      const now = Date.now();
      if (now - _authBroadcastedAt > 1500) {
        _authBroadcastedAt = now;
        try {
          try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
          import("@/lib/supabaseClient").then(({ supabase }) => {
            try { supabase?.auth?.signOut?.(); } catch (_e) { /* noop */ }
          }).catch(() => {});
          api.post("/auth/logout").catch(() => {});
          import("sonner").then(({ toast }) => {
            const lang = (typeof document !== "undefined" && document.documentElement.lang) || "de";
            const isDE = lang.startsWith("de");
            const msg = code === "account_deactivated" || raw === "account_deactivated"
              ? (isDE ? "Dieses Konto wurde deaktiviert." : "This account has been deactivated.")
              : (isDE ? "Deine Sitzung ist abgelaufen — bitte erneut einloggen." : "Your session expired — please sign in again.");
            toast.error(msg);
          }).catch(() => {});
          // Hard redirect breaks any in-flight render that assumes a user.
          if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
            setTimeout(() => { window.location.href = "/login"; }, 150);
          }
        } catch (_e) { /* never block the rejection chain */ }
      }
    }
    return Promise.reject(err);
  },
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);           // private identity (id, email, provider)
  const [profile, setProfile] = useState(null);     // public identity (nickname, country, language, games, avatar)
  const [booted, setBooted] = useState(false);

  const persist = useCallback((u, p) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: u, profile: p }));
    } catch (_e) {
      /* ignore */
    }
  }, []);

  // Hydrate both user + profile from AuthUser response
  const applyAuthUser = useCallback(
    (authUser) => {
      // AuthUser fields: user_id, email, email_verified, nickname, avatar,
      // country, language, favorite_games, completed_onboarding, provider,
      // role, is_admin, is_premium
      const u = {
        id: authUser.user_id,
        email: authUser.email,
        email_verified: !!authUser.email_verified,
        provider: authUser.provider || "email",
        role: authUser.role || "user",
        isAdmin: !!authUser.is_admin,
        isPremium: !!authUser.is_premium,
      };
      const p = {
        user_id: authUser.user_id,
        nickname: authUser.nickname || null,
        avatar: authUser.avatar || "",
        country: authUser.country || null,
        language: authUser.language || null,
        languages: authUser.languages || [],
        games: authUser.favorite_games || [],
        skill_level: authUser.skill_level || null,
        experience_level: authUser.experience_level || null,
        completed_onboarding: !!authUser.completed_onboarding,
      };
      setUser(u);
      setProfile(p);
      persist(u, p);
      return { user: u, profile: p };
    },
    [persist]
  );

  useEffect(() => {
    // Hydrate from cache first so guards don't flicker.
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.user) setUser(parsed.user);
        if (parsed.profile) setProfile(parsed.profile);
      }
    } catch (_e) {
      /* ignore */
    }

    // Skip /me check if we're mid-OAuth-callback; AuthCallback will handle.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setBooted(true);
      return;
    }

    api
      .get("/auth/me")
      .then((r) => {
        if (r.data?.user_id) applyAuthUser(r.data);
      })
      .catch(() => {
        // Not authenticated → drop stale cache (unless it's a guest session
        // only — we don't cache guests).
        setUser(null);
        setProfile(null);
        localStorage.removeItem(STORAGE_KEY);
      })
      .finally(() => setBooted(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Auth method wrappers ----------
  const registerEmail = useCallback(
    async ({ email, password, staySignedIn = true }) => {
      const r = await api.post("/auth/register", {
        email,
        password,
        stay_signed_in: staySignedIn,
      });
      return applyAuthUser(r.data);
    },
    [applyAuthUser]
  );

  const emailLogin = useCallback(
    async ({ email, password, staySignedIn = true }) => {
      const r = await api.post("/auth/email-login", {
        email,
        password,
        stay_signed_in: staySignedIn,
      });
      return applyAuthUser(r.data);
    },
    [applyAuthUser]
  );

  // Called by AuthCallback after exchanging session_id with backend.
  const loginFromSession = useCallback(
    (authUser) => applyAuthUser(authUser),
    [applyAuthUser]
  );

  const logout = useCallback(() => {
    // Clear local state immediately; fire-and-forget backend.
    setUser(null);
    setProfile(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
    // Best-effort: drop Supabase realtime session too so RLS-protected
    // channels disconnect immediately.
    try {
      import("@/lib/supabaseClient").then(({ supabase }) => {
        try { supabase?.auth?.signOut?.(); } catch (_e) { /* noop */ }
      }).catch(() => {});
    } catch (_e) { /* noop */ }
    api.post("/auth/logout").catch(() => { /* ignore */ });
  }, []);

  const saveProfile = useCallback(
    (p) => {
      // Merge with existing so partial updates don't wipe fields.
      setProfile((prev) => {
        const next = { ...(prev || {}), ...p };
        persist(user, next);
        return next;
      });
    },
    [persist, user]
  );

  const setPremium = useCallback(
    (isPremium) => {
      if (!user) return;
      const next = { ...user, isPremium };
      setUser(next);
      persist(next, profile);
    },
    [user, profile, persist]
  );

  const setNickname = useCallback(
    (nickname) => {
      setProfile((prev) => {
        const next = { ...(prev || {}), nickname };
        persist(user, next);
        return next;
      });
    },
    [persist, user]
  );

  // ---------- Back-compat helper (DEPRECATED mock path) ----------
  // Kept so legacy screens importing `login` keep compiling during the
  // refactor window. New code should call emailLogin/registerEmail or the
  // Google OAuth flow.
  const login = useCallback(
    ({ email, method = "email" }) => {
      const userId = `u_${Math.random().toString(36).slice(2, 10)}`;
      const u = {
        id: userId,
        email: email || `${method}@ficopilot.local`,
        provider: method,
        role: "user",
        isAdmin: false,
        isPremium: false,
      };
      const p = {
        user_id: userId,
        nickname: null,
        avatar: "",
        country: null,
        language: null,
        games: [],
        completed_onboarding: false,
      };
      setUser(u);
      setProfile(p);
      persist(u, p);
      return u;
    },
    [persist]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        booted,
        login,                // deprecated
        registerEmail,
        emailLogin,
        loginFromSession,
        logout,
        saveProfile,
        setPremium,
        setNickname,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Shared helper for readable API error messages (FastAPI 422 returns objects).
// Returns null when no useful detail is present so callers can fall back to
// `err.message` (which contains "Network Error" / "timeout of 25000ms exceeded"
// / etc.) instead of always showing a generic "something went wrong" toast.
export function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ") || null;
  }
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.error === "string") return detail.error;
  return String(detail);
}

/**
 * Maps raw API error codes/messages to clean, user-friendly German+English
 * copy. Anywhere a `try { api.post(...) } catch (e)` ends in a toast, run the
 * detail through this function FIRST so the user never sees raw codes like
 * `account_deactivated` or `not_authenticated`.
 *
 * @param {*} detail   The `error.response.data.detail` value (string|object)
 * @param {string} lang  "de" or "en"  (defaults to "de" — primary audience)
 * @returns {string}   Friendly message ready for toast.error()
 */
export function friendlyAuthError(detail, lang = "de") {
  const isDE = !lang || lang.startsWith("de");
  const code = formatApiErrorDetail(detail) || "";
  const c = code.toLowerCase();
  if (c === "account_deactivated" || c.includes("account_deactivated")) {
    return isDE ? "Dieses Konto wurde deaktiviert." : "This account has been deactivated.";
  }
  if (c === "not_authenticated" || c === "auth_required" || c.includes("not_authenticated")) {
    return isDE ? "Deine Sitzung ist abgelaufen — bitte erneut einloggen." : "Your session expired — please sign in again.";
  }
  if (c === "invalid_credentials") {
    return isDE ? "E-Mail oder Passwort ist falsch." : "Wrong email or password.";
  }
  if (c === "email_taken") {
    return isDE ? "Diese E-Mail ist bereits registriert. Stattdessen einloggen?" : "This email is already registered. Sign in instead?";
  }
  if (c === "invalid_or_used_token" || c === "invalid_token") {
    return isDE ? "Link ist ungültig oder wurde bereits verwendet." : "Link is invalid or already used.";
  }
  if (c === "token_expired") {
    return isDE ? "Link ist abgelaufen. Bitte neuen anfordern." : "Link expired. Please request a new one.";
  }
  // Last resort: show the raw code so we can still debug, but cleaned up.
  return code || (isDE ? "Etwas ist schiefgelaufen." : "Something went wrong.");
}
